import { Node, Edge } from '@xyflow/react';
import { FlowNodeData } from '../components/Windmill/CustomNodes';

// Types for OpenFlow data structure
interface OpenFlowModule {
  id: string;
  value: {
    path?: string;
    type: 'script' | 'branchone' | 'rawscript' | 'branchall';
    is_trigger?: boolean;
    input_transforms?: Record<string, any>;
    content?: string;
    language?: string;
    default?: OpenFlowModule[];
    branches?: {
      expr: string;
      modules: OpenFlowModule[];
      summary?: string;
      parallel?: boolean;
      skip_failure?: boolean;
    }[];
  };
}

interface OpenFlowData {
  raw_flow: {
    modules: OpenFlowModule[];
    failure_module?: OpenFlowModule;
  };
  flow_status: {
    modules: {
      id: string;
      job?: string;
      type: string;
      skipped?: boolean;
      branch_chosen?: {
        type: string;
        branch: number;
      };
      parent_module?: string; // Added parent_module for failure node positioning
      // Add these properties for branchall support
      flow_jobs?: string[];
      flow_jobs_success?: boolean[];
    }[];
    failure_module?: {
      id: string;
      type: string;
      job?: string;
      parent_module?: string;
    };
    aggregated_failure_modules?: {
      id: string;
      type: string;
      job: string;
      parent_module: string;
      source_job_id?: string; // Track which nested job this came from
      skipped?: boolean;
    }[];
  };
  workspace_id: string;
  path: string;
  summary: string;
  description: string;
  value: {
    modules: OpenFlowModule[];
    failure_module?: OpenFlowModule;
  };
}

type FlowNode = Node<FlowNodeData & Record<string, unknown>>;

// Position calculation for vertical main flow with horizontal branching
const calculateNodePosition = (
  globalIndex: number,
  isBranch = false,
  branchIndex = 0,
  isConvergence = false,
  isErrorHandler = false,
  isFailureNode = false,
  parentPosition?: { x: number; y: number },
  branchBaseY?: number,
  localBranchIndex?: number,
  totalBranches?: number
): { x: number; y: number } => {
  // Increased spacing for better visual separation
  const verticalSpacing = 140;
  const horizontalSpacing = 500; // Increased from 400 to 500 for more gap

  if (isErrorHandler) {
    return {
      x: -300,
      y: 60,
    };
  }

  if (isFailureNode && parentPosition) {
    return {
      x: parentPosition.x + 280,
      y: parentPosition.y + 80,
    };
  }

  if (isConvergence) {
    return {
      x: 0,
      y: globalIndex * verticalSpacing,
    };
  }

  if (isBranch) {
    // Improved branch positioning logic for multiple branches
    let horizontalOffset = 0;

    if (totalBranches && totalBranches > 0) {
      // Calculate positions to distribute branches evenly with more spacing
      const totalWidth = (totalBranches - 1) * horizontalSpacing;
      const startX = -totalWidth / 2;
      horizontalOffset = startX + branchIndex * horizontalSpacing;
    } else {
      // Improved fallback for when totalBranches is not provided
      // Handle 2 branches case properly to avoid overlap with center (x=0)
      if (branchIndex === 0) {
        horizontalOffset = -horizontalSpacing; // Left side
      } else if (branchIndex === 1) {
        horizontalOffset = horizontalSpacing; // Right side (not center)
      } else {
        // For 3+ branches, use the original logic
        horizontalOffset =
          branchIndex === 2 ? horizontalSpacing * 2 : horizontalSpacing;
      }
    }

    // If branchBaseY is provided, use it with local positioning for branch modules
    if (branchBaseY !== undefined) {
      const yOffset =
        localBranchIndex !== undefined ? localBranchIndex * verticalSpacing : 0;
      return {
        x: horizontalOffset,
        y: branchBaseY + yOffset,
      };
    }

    // Fallback to global positioning
    return {
      x: horizontalOffset,
      y: globalIndex * verticalSpacing,
    };
  }

  // Main flow nodes positioned vertically in center
  return {
    x: 0,
    y: globalIndex * verticalSpacing,
  };
};

// Extract tag from path for better visual representation
const extractTagFromPath = (path?: string): string => {
  if (!path) return '';

  const parts = path.split('/');
  const lastPart = parts[parts.length - 1];

  return lastPart.substring(0, 8) + '...';
};

// Global position counter
let globalPositionIndex = 0;

// Helper function to determine inherited status for branch children
const getInheritedStatus = (
  branchIndex: number | 'default',
  chosenBranch: number | null,
  parentStatus: string
): string | undefined => {
  // If no branch was chosen, return undefined (no status)
  if (chosenBranch === null) {
    return undefined;
  }

  // If this is the chosen branch, inherit parent's status
  if (branchIndex === chosenBranch) {
    return parentStatus;
  }

  // If this is default branch and it was chosen (chosenBranch would be -1 or a special value)
  // Note: We need to check how default branches are represented in the API
  if (branchIndex === 'default' && chosenBranch === -1) {
    return parentStatus;
  }

  // For non-chosen branches, they have no status (didn't run)
  return undefined;
};

// Recursive function to convert OpenFlow modules to React Flow nodes and edges
const convertModulesToReactFlow = (
  modules: OpenFlowModule[],
  precedingNodeId: string | null = null,
  level = 0,
  isBranch = false,
  branchIndex = 0,
  modulesBranchChoice?: Record<string, number | null>,
  modulesStatus?: Record<string, string>,
  modulesJobs?: Record<string, string>,
  modulesBranchallSuccess?: Record<string, boolean[]>,
  parentBranchInfo?: {
    parentModuleId: string;
    branchIndex: number | 'default';
    inheritedStatus: string | undefined;
    parentJob?: string;
  },
  branchBaseY?: number,
  localBranchStartIndex = 0,
  // Add new parameters for branchall job mapping
  branchallJobsMapping?: Record<string, string[]>, // Maps parent module ID to flow_jobs array
  currentBranchJobIndex?: number, // Current branch's job index
  totalBranches?: number // Add the new parameter
): { nodes: FlowNode[]; edges: Edge[]; lastNodeId: string | null } => {
  const nodes: FlowNode[] = [];
  const edges: Edge[] = [];
  let lastNodeId: string | null = precedingNodeId;
  let localBranchIndex = localBranchStartIndex;

  for (const module of modules) {
    if (module.value.type === 'script' || module.value.type === 'rawscript') {
      // Use branch positioning if we're in a branch and have branchBaseY
      const position =
        isBranch && branchBaseY !== undefined
          ? calculateNodePosition(
              0, // Not using globalIndex
              isBranch,
              branchIndex,
              false,
              false,
              undefined,
              undefined,
              branchBaseY,
              localBranchIndex++,
              totalBranches // Pass totalBranches
            )
          : calculateNodePosition(
              globalPositionIndex++,
              isBranch,
              branchIndex,
              false,
              false,
              undefined,
              undefined,
              undefined,
              undefined,
              totalBranches // Pass totalBranches
            );

      // Determine status and job: prioritize module's own status, then branchall mapping, then inherited status
      let nodeStatus: string | undefined =
        modulesStatus?.[module.id] || undefined;
      let nodeJob: string | undefined = modulesJobs?.[module.id] || undefined;

      // NEW: For branchall, if module doesn't have its own status/job, use the branch-specific ones
      if (
        parentBranchInfo &&
        !nodeStatus &&
        !nodeJob &&
        branchallJobsMapping &&
        currentBranchJobIndex !== undefined
      ) {
        const parentFlowJobs =
          branchallJobsMapping[parentBranchInfo.parentModuleId];
        const parentFlowJobsSuccess =
          modulesBranchallSuccess?.[parentBranchInfo.parentModuleId];

        if (
          parentFlowJobs &&
          parentFlowJobsSuccess &&
          currentBranchJobIndex < parentFlowJobs.length
        ) {
          nodeJob = parentFlowJobs[currentBranchJobIndex];
          nodeStatus = parentFlowJobsSuccess[currentBranchJobIndex]
            ? 'success'
            : 'failure';
        }
      }

      // Fallback to inherited status/job if still not set
      if (parentBranchInfo) {
        if (!nodeStatus) {
          nodeStatus = parentBranchInfo.inheritedStatus;
        }
        if (!nodeJob && parentBranchInfo.parentJob) {
          nodeJob = parentBranchInfo.parentJob;
        }
      }

      const node: FlowNode = {
        id: module.id,
        type: 'scriptNode',
        position,
        data: {
          label: module.id,
          type: module.value.type,
          path: module.value.path,
          input_transforms: module.value.input_transforms,
          content: module.value.content,
          language: module.value.language,
          status: nodeStatus || undefined,
          job: nodeJob || undefined,
          isTrigger: module.value.is_trigger || false,
          tag: extractTagFromPath(module.value.path),
        },
      };

      nodes.push(node);

      // Create edge from preceding node
      if (lastNodeId) {
        const edge: Edge = {
          id: `${lastNodeId}-${module.id}`,
          source: lastNodeId,
          target: module.id,
          type: 'custom',
          data: { label: '' },
        };
        edges.push(edge);
      }

      lastNodeId = module.id;
    } else if (
      module.value.type === 'branchone' ||
      module.value.type === 'branchall'
    ) {
      const handles: { id: string; label: string }[] = [];

      if (module.value.branches) {
        module.value.branches.forEach((branch, i) => {
          handles.push({
            id: `branch-${i}`,
            label: branch.summary || `Branch ${i + 1}`,
          });
        });
      }

      if (module.value.default && module.value.default.length > 0) {
        handles.push({
          id: 'default',
          label: 'Default',
        });
      }

      // Get the selected branch, status, and job for this specific module
      const thisModuleSelectedBranch = modulesBranchChoice?.[module.id] ?? null;
      const thisModuleStatus = modulesStatus?.[module.id] || undefined;
      const thisModuleJob = modulesJobs?.[module.id] || undefined;

      // Create condition node
      const conditionPosition = calculateNodePosition(
        globalPositionIndex++,
        isBranch,
        branchIndex,
        false,
        false,
        undefined,
        undefined,
        undefined,
        undefined,
        totalBranches // Pass totalBranches
      );

      const conditionNode: FlowNode = {
        id: module.id,
        type:
          module.value.type === 'branchall' ? 'parallelNode' : 'conditionNode',
        position: conditionPosition,
        data: {
          label: module.id,
          type: module.value.type,
          value: module.value,
          status: thisModuleStatus || undefined,
          job: thisModuleJob || undefined,
          summary:
            module.value.type === 'branchall'
              ? 'Parallel Branch'
              : 'Conditional Branch',
          tag: '',
          handles,
          selectedConditionalBranch: thisModuleSelectedBranch,
        },
      };

      nodes.push(conditionNode);

      // Create edge from preceding node to condition
      if (lastNodeId) {
        const edge: Edge = {
          id: `${lastNodeId}-${module.id}`,
          source: lastNodeId,
          target: module.id,
          type: 'custom',
          data: { label: '' },
        };
        edges.push(edge);
      }

      const branchEndNodes: string[] = [];

      // Process branches
      if (module.value.branches) {
        // Calculate the base Y position for all branches (one level below the conditional)
        const branchBaseY = conditionPosition.y + 140; // Updated spacing
        // Include default branch in total count for proper positioning
        const hasDefault =
          module.value.default && module.value.default.length > 0;
        const totalBranches =
          module.value.branches.length + (hasDefault ? 1 : 0);

        for (let i = 0; i < module.value.branches.length; i++) {
          const branch = module.value.branches[i];
          // Position branch node at the same Y level for all branches
          const branchPosition = calculateNodePosition(
            0, // Not using globalIndex for branches
            true,
            i,
            false,
            false,
            undefined,
            undefined,
            branchBaseY,
            0, // This is the branch header node
            totalBranches // Pass total number of branches including default
          );

          // Handle branchall vs branchone differently
          let branchInheritedStatus: string | undefined;
          let branchJob: string | undefined;

          if (module.value.type === 'branchall') {
            // For branchall, use flow_jobs_success array
            const branchSuccessArray = modulesBranchallSuccess?.[module.id];
            const branchJobsArray = branchallJobsMapping?.[module.id];

            if (branchSuccessArray && branchSuccessArray[i] !== undefined) {
              branchInheritedStatus = branchSuccessArray[i]
                ? 'success'
                : 'failure';
            }

            if (branchJobsArray && branchJobsArray[i]) {
              branchJob = branchJobsArray[i];
            }

            // Fallback to parent status if no specific branch info
            if (branchInheritedStatus === undefined) {
              branchInheritedStatus = thisModuleStatus || undefined;
            }
          } else {
            // For branchone, use existing logic
            branchInheritedStatus = getInheritedStatus(
              i,
              thisModuleSelectedBranch,
              thisModuleStatus || ''
            );
            branchJob = thisModuleJob;
          }

          // Create branch condition node
          const branchNode: FlowNode = {
            id: `${module.id}-branch-${i}`,
            type: 'branchNode',
            position: branchPosition,
            data: {
              label: branch.summary || `Branch ${i + 1}`,
              type: module.value.type,
              status: branchInheritedStatus || undefined,
              job: branchJob || undefined,
              expression: branch.expr,
              summary: branch.summary,
              tag: extractTagFromPath(branch.summary),
            },
          };

          nodes.push(branchNode);

          // Create edge from condition to branch
          const branchEdge: Edge = {
            id: `${module.id}-${module.id}-branch-${i}`,
            source: module.id,
            target: `${module.id}-branch-${i}`,
            sourceHandle: `branch-${i}`,
            type: 'custom',
            data: {
              label:
                branch.expr ||
                (module.value.type === 'branchall' ? 'parallel' : ''),
            },
          };
          edges.push(branchEdge);

          // Recursively process branch modules with inherited status and job
          if (branch.modules && branch.modules.length > 0) {
            const branchResult = convertModulesToReactFlow(
              branch.modules,
              `${module.id}-branch-${i}`,
              level + 2,
              true,
              i,
              modulesBranchChoice,
              modulesStatus,
              modulesJobs,
              modulesBranchallSuccess,
              {
                parentModuleId: module.id,
                branchIndex: i,
                inheritedStatus: branchInheritedStatus,
                parentJob: branchJob,
              },
              branchBaseY + 140, // Pass the branch base Y for consistent positioning
              1, // Start local branch index at 1 (since 0 is the branch header)
              branchallJobsMapping, // Pass the job mapping
              i, // Pass the current branch index for job lookup
              totalBranches // Pass total branches to recursive calls
            );
            nodes.push(...branchResult.nodes);
            edges.push(...branchResult.edges);

            // Track the last node in this branch
            if (branchResult.lastNodeId) {
              branchEndNodes.push(branchResult.lastNodeId);
            }
          } else {
            // If no modules in branch, the branch node itself is the end
            branchEndNodes.push(`${module.id}-branch-${i}`);
          }
        }
      }

      // Process default branch
      if (module.value.default && module.value.default.length > 0) {
        // Calculate total branches including default for proper positioning
        const regularBranchCount = module.value.branches
          ? module.value.branches.length
          : 0;
        const totalBranches = regularBranchCount + 1; // +1 for default branch
        const defaultBranchIndex = regularBranchCount; // Default branch comes after all regular branches

        const defaultPosition = calculateNodePosition(
          globalPositionIndex++,
          true,
          defaultBranchIndex, // Use correct index instead of hardcoded 1
          false,
          false,
          undefined,
          undefined,
          undefined,
          undefined,
          totalBranches // Pass correct totalBranches
        );

        // Determine status for default branch
        const defaultInheritedStatus = getInheritedStatus(
          'default',
          thisModuleSelectedBranch,
          thisModuleStatus || ''
        );

        const defaultNode: FlowNode = {
          id: `${module.id}-default`,
          type: 'defaultNode',
          position: defaultPosition,
          data: {
            label: 'Default',
            type: 'branchone',
            status: defaultInheritedStatus || undefined,
            job: thisModuleJob || undefined,
            summary: 'Default Path',
          },
        };

        nodes.push(defaultNode);

        // Create edge from condition to default
        const defaultEdge: Edge = {
          id: `${module.id}-${module.id}-default`,
          source: module.id,
          target: `${module.id}-default`,
          sourceHandle: 'default',
          type: 'custom',
          data: { label: 'Default', isDefault: true },
        };
        edges.push(defaultEdge);

        // Recursively process default modules with inherited status and job
        const defaultResult = convertModulesToReactFlow(
          module.value.default,
          `${module.id}-default`,
          level + 2,
          true,
          defaultBranchIndex, // Use correct branch index
          modulesBranchChoice,
          modulesStatus,
          modulesJobs,
          modulesBranchallSuccess,
          {
            parentModuleId: module.id,
            branchIndex: 'default',
            inheritedStatus: defaultInheritedStatus,
            parentJob: thisModuleJob,
          },
          undefined,
          0,
          branchallJobsMapping,
          undefined, // No currentBranchJobIndex for default branch
          totalBranches // Pass totalBranches
        );
        nodes.push(...defaultResult.nodes);
        edges.push(...defaultResult.edges);

        if (defaultResult.lastNodeId) {
          branchEndNodes.push(defaultResult.lastNodeId);
        }
      }

      // Create convergence node - Position it below the branches
      let maxBranchDepth = branchBaseY || 0;

      // Find the deepest Y position among all branch-related nodes
      branchEndNodes.forEach(endNodeId => {
        const endNode = nodes.find(node => node.id === endNodeId);
        if (endNode && endNode.position.y > maxBranchDepth) {
          maxBranchDepth = endNode.position.y;
        }
      });

      const convergencePosition = {
        x: 0,
        y: maxBranchDepth + 140, // Position below the deepest branch node
      };

      const convergenceNode: FlowNode = {
        id: `${module.id}-convergence`,
        type: 'convergenceNode',
        position: convergencePosition,
        data: {
          label:
            module.value.type === 'branchall'
              ? 'Collect results from all branches'
              : 'Collect result from chosen branch',
          type: 'script',
          status: thisModuleStatus || undefined,
          job: thisModuleJob || undefined,
          tag: 'converge...',
        },
      };

      nodes.push(convergenceNode);

      // Create edges from all branch end nodes to convergence
      branchEndNodes.forEach(endNodeId => {
        const convergenceEdge: Edge = {
          id: `${endNodeId}-${module.id}-convergence`,
          source: endNodeId,
          target: `${module.id}-convergence`,
          type: 'custom',
          data: { label: '' },
        };
        edges.push(convergenceEdge);
      });

      lastNodeId = `${module.id}-convergence`;
    }
  }

  return { nodes, edges, lastNodeId };
};

// Main conversion function
export const convertOpenFlowToReactFlow = (
  flowData: OpenFlowData,
  mainJobData?: any,
  isJobDetail = false
): {
  nodes: FlowNode[];
  edges: Edge[];
} => {
  const modules = flowData?.value?.modules || flowData?.raw_flow?.modules;
  if (!modules?.length) {
    return { nodes: [], edges: [] };
  }

  const modulesStatus: Record<string, string> = {};
  const modulesJobs: Record<string, string> = {};
  const modulesBranchChoice: Record<string, number | null> = {};
  const modulesBranchallSuccess: Record<string, boolean[]> = {};
  // NEW: Create mapping for branchall flow_jobs
  const branchallJobsMapping: Record<string, string[]> = {};

  flowData?.flow_status?.modules?.forEach(mod => {
    modulesStatus[mod.id] =
      mod.type === 'Success'
        ? 'success'
        : mod.type === 'Failure'
          ? 'failure'
          : mod.type === 'InProgress'
            ? 'running'
            : mod.type === 'WaitingForPriorSteps'
              ? 'waiting'
              : 'default';

    // Store job information for this module
    if (mod.job) {
      modulesJobs[mod.id] = mod.job;
    }

    // Store branch choice for this specific module
    if (mod.branch_chosen) {
      modulesBranchChoice[mod.id] = mod.branch_chosen.branch;
    }

    // Store flow_jobs_success for branchall modules
    if (mod.flow_jobs_success) {
      modulesBranchallSuccess[mod.id] = mod.flow_jobs_success;
    }

    // NEW: Store flow_jobs mapping for branchall modules
    if (mod.flow_jobs) {
      branchallJobsMapping[mod.id] = mod.flow_jobs;
    }
  });

  // Reset global position counter
  globalPositionIndex = 0;

  const nodes: FlowNode[] = [];
  const edges: Edge[] = [];
  let lastNodeForTrigger: string | null = null;

  if (!isJobDetail) {
    // Add Trigger node at the beginning
    const triggerNode: FlowNode = {
      id: 'trigger-node',
      type: 'triggerNode',
      position: calculateNodePosition(
        globalPositionIndex++,
        false,
        0,
        false,
        false,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined // No totalBranches for trigger node
      ),
      data: {
        label: 'Triggers',
        type: 'trigger',
        status: 'completed',
        job: undefined,
        flowData: flowData,
      },
    };
    nodes.push(triggerNode);
    lastNodeForTrigger = 'trigger-node';
  }

  // Add Input node after trigger with main job data
  const inputNode: FlowNode = {
    id: 'input-node',
    type: 'inputNode',
    position: calculateNodePosition(
      globalPositionIndex++,
      false,
      0,
      false,
      false,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined // No totalBranches for input node
    ),
    data: {
      label: 'Input',
      type: 'script',
      status: 'completed',
      job: mainJobData?.id,
      isMainJobNode: true,
      mainJobData: mainJobData,
      flowData: flowData,
      isJobDetail: isJobDetail,
    },
  };
  nodes.push(inputNode);

  // Create edge from trigger to input
  if (lastNodeForTrigger) {
    const triggerToInputEdge: Edge = {
      id: 'trigger-to-input',
      source: lastNodeForTrigger || '',
      target: 'input-node',
      type: 'custom',
      data: { label: '' },
    };
    edges.push(triggerToInputEdge);
  }

  // Convert main modules starting from input node, passing all the mappings including branchall jobs
  const mainResult = convertModulesToReactFlow(
    flowData?.value?.modules || flowData?.raw_flow?.modules,
    'input-node',
    0,
    false,
    0,
    modulesBranchChoice,
    modulesStatus,
    modulesJobs,
    modulesBranchallSuccess,
    undefined,
    undefined,
    0,
    branchallJobsMapping, // Pass the new branchall jobs mapping
    undefined, // No currentBranchJobIndex for main modules
    undefined // No totalBranches for main modules
  );

  nodes.push(...mainResult.nodes);
  edges.push(...mainResult.edges);

  // Handle failure modules from aggregated nested jobs (prioritize over main job failure module)
  const failureModulesToProcess = [];

  // Check if we have aggregated failure modules from nested jobs
  if ((flowData?.flow_status?.aggregated_failure_modules?.length ?? 0) > 0) {
    // Use aggregated failure modules from nested jobs
    failureModulesToProcess.push(
      ...(flowData.flow_status.aggregated_failure_modules ?? [])
    );
  } else if (
    // Fallback to main job failure module only if it has meaningful data
    flowData?.flow_status?.failure_module &&
    flowData.flow_status.failure_module.job &&
    flowData.flow_status.failure_module.parent_module &&
    mainJobData?.success === false
  ) {
    failureModulesToProcess.push(flowData.flow_status.failure_module);
  }

  // Process each failure module that has meaningful data
  failureModulesToProcess.forEach((failureModule, index) => {
    // Skip failure modules without job or parent_module
    if (!failureModule.job || !failureModule.parent_module) {
      return;
    }

    const parentModuleId = failureModule.parent_module;

    // Find the parent module to position the failure node beside it
    const parentNode = nodes.find(node => node.id === parentModuleId);

    if (parentNode) {
      // Create unique ID for multiple failure nodes
      const failureNodeId =
        failureModulesToProcess.length > 1
          ? `${parentModuleId}-failure-${index}`
          : `${parentModuleId}-failure`;

      const failureNode: FlowNode = {
        id: failureNodeId,
        type: 'errorHandlerNode',
        position: calculateNodePosition(
          0,
          false,
          0,
          false,
          false,
          true, // isFailureNode
          parentNode.position,
          undefined,
          undefined,
          undefined // No totalBranches for failure node
        ),
        data: {
          label: 'Failure Handler',
          type: 'rawscript',
          status:
            failureModule.type === 'Success'
              ? 'success'
              : failureModule.type === 'Failure'
                ? 'failure'
                : failureModule.type === 'InProgress'
                  ? 'running'
                  : failureModule.type === 'WaitingForPriorSteps'
                    ? 'waiting'
                    : 'default',
          job: failureModule.job,
          language: 'python',
          parentModule: parentModuleId,
          sourceJobId: (failureModule as any).source_job_id ?? undefined, // Add context about source
        },
      };

      nodes.push(failureNode);

      // Create edge from parent module to failure node
      const failureEdge: Edge = {
        id: `${parentModuleId}-to-failure-${index}`,
        source: parentModuleId || '',
        target: failureNodeId,
        type: 'custom',
        data: { label: 'error', isError: true },
        style: { stroke: '#f44336', strokeDasharray: '5,5' },
      };

      edges.push(failureEdge);
    }
  });

  // Add Result node at the end with main job data and success status
  const resultStatus =
    isJobDetail && mainJobData?.success !== undefined
      ? mainJobData.success
        ? 'success'
        : 'failure'
      : undefined;

  // Find the maximum Y position among all nodes to position result below
  const maxY = Math.max(...nodes.map(node => node.position.y));

  const resultNode: FlowNode = {
    id: 'result-node',
    type: 'resultNode',
    position: {
      x: 0,
      y: maxY + 140, // Position below the deepest node
    },
    data: {
      label: 'Result',
      type: 'script',
      status: resultStatus,
      job: mainJobData?.id,
      isMainJobNode: true,
      mainJobData: mainJobData,
    },
  };
  nodes.push(resultNode);

  // Create edge from last module to result
  if (mainResult.lastNodeId) {
    const lastToResultEdge: Edge = {
      id: `${mainResult.lastNodeId}-to-result`,
      source: mainResult.lastNodeId,
      target: 'result-node',
      type: 'custom',
      data: { label: '' },
    };
    edges.push(lastToResultEdge);
  } else {
    // If no modules, connect input directly to result
    const inputToResultEdge: Edge = {
      id: 'input-to-result',
      source: 'input-node',
      target: 'result-node',
      type: 'custom',
      data: { label: '' },
    };
    edges.push(inputToResultEdge);
  }

  return { nodes, edges };
};
